import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Link,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  InputAdornment,
  Chip,
} from '@mui/material';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
  Business as BusinessIcon,
  PersonPin as PersonPinIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { SignupRequest, UserType } from '../types';
import {
  isValidEmail,
  isValidPhoneNumber,
  getEmailErrorMessage,
  getPhoneNumberErrorMessage,
  getPasswordRuleStatus,
  getPasswordErrorMessage,
  PASSWORD_REQUIREMENTS_HINT,
  normalizeTenDigitPhone,
} from '../utils/validation';
import AuthService from '../services/authService';
import VerificationService from '../services/verificationService';
import AuthShell, { authFormSurfaceSx } from './AuthShell';

const Signup: React.FC = () => {
  const [formData, setFormData] = useState<SignupRequest>({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    password: '',
    userType: UserType.CLIENT,
  });
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [phoneOtp, setPhoneOtp] = useState('');
  const [emailOtp, setEmailOtp] = useState('');
  const [phoneOtpSent, setPhoneOtpSent] = useState(false);
  const [emailOtpSent, setEmailOtpSent] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [passwordError, setPasswordError] = useState('');

  const { signup } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (name === 'phoneNumber') {
      const digitsOnly = value.replace(/\D/g, '').slice(0, 10);
      setFormData(prev => ({
        ...prev,
        phoneNumber: digitsOnly,
      }));
      if (error) setError('');
      if (phoneVerified) setPhoneVerified(false);
      if (phoneOtpSent) {
        setPhoneOtpSent(false);
        setPhoneOtp('');
      }
      if (phoneError && phoneError.includes('already registered')) {
        setPhoneError('');
      } else {
        setPhoneError(getPhoneNumberErrorMessage(digitsOnly));
      }
      return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));

    if (error) {
      setError('');
    }

    if (name === 'email') {
      if (emailVerified) {
        setEmailVerified(false);
      }
      if (emailOtpSent) {
        setEmailOtpSent(false);
        setEmailOtp('');
      }
      // Clear email error if it was a duplicate error and user is typing
      if (emailError && emailError.includes('already registered')) {
        setEmailError('');
      } else {
        setEmailError(getEmailErrorMessage(value));
      }
    }

    // firstName / lastName: no extra validation
    if (name === 'firstName' || name === 'lastName') {
      return;
    }

    if (name === 'password') {
      setPasswordError('');
    }
  };

  const handleUserTypeChange = (e: any) => {
    setFormData(prev => ({
      ...prev,
      userType: e.target.value as UserType
    }));
  };

  const handleSendEmailOtp = async () => {
    // Validate email format first
    const emailErr = getEmailErrorMessage(formData.email);
    setEmailError(emailErr);
    if (emailErr) {
      setError(emailErr);
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Check if email already exists
      const emailExists = await AuthService.checkEmail(formData.email);
      if (emailExists) {
        setError('This email address is already registered. Please use a different email or try logging in.');
        setEmailError('This email address is already registered');
        setLoading(false);
        return;
      }

      await VerificationService.sendSignupEmailOtp(formData.email);
      setEmailOtp('');
      setEmailOtpSent(true);
    } catch (err: any) {
      console.error('Error sending email OTP:', err);
      const message = err?.response?.data?.error || 'Failed to send email OTP. Please try again.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleSendPhoneOtp = async () => {
    // Validate phone number format first
    const phoneErr = getPhoneNumberErrorMessage(formData.phoneNumber);
    setPhoneError(phoneErr);
    if (phoneErr) {
      setError(phoneErr);
      return;
    }

    try {
      setLoading(true);
      setError('');

      const normalized = normalizeTenDigitPhone(formData.phoneNumber);
      const phoneExists = await AuthService.checkPhoneNumber(normalized);
      if (phoneExists) {
        setError('This phone number is already registered. Please use a different phone number or try logging in.');
        setPhoneError('This phone number is already registered');
        setLoading(false);
        return;
      }

      await VerificationService.sendSignupPhoneOtp(normalized);
      setPhoneOtp('');
      setPhoneOtpSent(true);
    } catch (err: any) {
      console.error('Error sending phone OTP:', err);
      const message = err?.response?.data?.error || 'Failed to send phone OTP. Please try again.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyEmailOtp = async () => {
    if (!emailOtpSent) {
      setError('Please request an email OTP first.');
      return;
    }
    if (!emailOtp.trim()) {
      setError('Please enter the OTP sent to your email.');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      // Email OTP verified - mark as verified
      const response = await VerificationService.verifySignupEmailOtp(formData.email, emailOtp.trim());
      if (response?.verified) {
        setEmailVerified(true);
        setEmailOtp('');
      } else {
        setError('Invalid or expired email OTP. Please try again.');
      }
    } catch (err: any) {
      console.error('Error verifying email OTP:', err);
      const message = err?.response?.data?.error || 'Failed to verify email OTP. Please try again.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyPhoneOtp = async () => {
    if (!phoneOtpSent) {
      setError('Please request a phone OTP first.');
      return;
    }
    if (!phoneOtp.trim()) {
      setError('Please enter the OTP sent to your phone.');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      // Phone OTP verified - mark as verified
      const response = await VerificationService.verifySignupPhoneOtp(
        normalizeTenDigitPhone(formData.phoneNumber),
        phoneOtp.trim()
      );
      if (response?.verified) {
        setPhoneVerified(true);
        setPhoneOtp('');
      } else {
        setError('Invalid or expired phone OTP. Please try again.');
      }
    } catch (err: any) {
      console.error('Error verifying phone OTP:', err);
      const message = err?.response?.data?.error || 'Failed to verify phone OTP. Please try again.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteSignup = async () => {
    // Check if both email and phone are verified
    if (!emailVerified || !phoneVerified) {
      setError('Please verify both email and phone number before completing signup.');
      return;
    }

    const signupPwdErr = getPasswordErrorMessage(formData.password);
    if (signupPwdErr) {
      setPasswordError(signupPwdErr);
      setError(signupPwdErr);
      return;
    }
    if (formData.password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      const signupData = {
        ...formData,
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim(),
        phoneNumber: normalizeTenDigitPhone(formData.phoneNumber),
      };
      
      // Both OTPs verified – proceed with backend signup
      await signup(signupData);
      navigate(
        formData.userType === 'CLIENT'
          ? '/explore'
          : formData.userType === 'VENDOR'
          ? '/vendor-dashboard'
          : '/explore'
      );
    } catch (err: any) {
      console.error('Error completing signup:', err);

      // Extract error message from backend response
      let errorMessage = 'Signup failed. Please try again.';
      const errorData = err?.response?.data;
      
      if (errorData) {
        // Check for validation errors with fieldErrors
        if (errorData.fieldErrors && Object.keys(errorData.fieldErrors).length > 0) {
          const firstFieldError = Object.values(errorData.fieldErrors)[0] as string;
          errorMessage = firstFieldError || errorData.message || errorData.error || errorMessage;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.error) {
          errorMessage = errorData.error;
          
          // Check for specific duplicate phone/email messages and set field-specific errors
          if (errorMessage.includes('Phone number is already taken') || 
              errorMessage.includes('phone number is already taken')) {
            setPhoneError('This phone number is already registered');
            errorMessage = 'This phone number is already registered. Please use a different phone number or try logging in.';
          } else if (errorMessage.includes('Email is already in use') || 
                     errorMessage.includes('email is already in use')) {
            setEmailError('This email address is already registered');
            errorMessage = 'This email address is already registered. Please use a different email or try logging in.';
          }
        } else {
          errorMessage = 'Signup failed. Please try again.';
        }
      } else if (err?.message) {
        errorMessage = err.message;
        
        // Also check err.message for duplicate phone/email
        if (errorMessage.includes('Phone number is already taken') || 
            errorMessage.includes('phone number is already taken')) {
          setPhoneError('This phone number is already registered');
          errorMessage = 'This phone number is already registered. Please use a different phone number or try logging in.';
        } else if (errorMessage.includes('Email is already in use') || 
                   errorMessage.includes('email is already in use')) {
          setEmailError('This email address is already registered');
          errorMessage = 'This email address is already registered. Please use a different email or try logging in.';
        }
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    if (
      !formData.firstName ||
      !formData.lastName ||
      !formData.email ||
      !formData.phoneNumber ||
      !formData.password ||
      !confirmPassword
    ) {
      setError('Please fill in all fields');
      return;
    }

    // Validate email
    const emailErr = getEmailErrorMessage(formData.email);
    setEmailError(emailErr);
    if (emailErr) {
      setError(emailErr);
      return;
    }

    // Validate phone number
    const phoneErr = getPhoneNumberErrorMessage(formData.phoneNumber);
    setPhoneError(phoneErr);
    if (phoneErr) {
      setError(phoneErr);
      return;
    }

    if (formData.password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    const pwdErr = getPasswordErrorMessage(formData.password);
    if (pwdErr) {
      setPasswordError(pwdErr);
      setError(pwdErr);
      return;
    }

    // Check if both are verified
    if (emailVerified && phoneVerified) {
      // Both verified, complete signup
      await handleCompleteSignup();
    } else {
      // Show message to verify both
      setError('Please verify both your email and phone number before completing signup.');
    }
  };

  return (
    <AuthShell title="Join Us Today! 🎊" subtitle="Create your account and start planning amazing events">
      {error && (
        <Box style={{ width: '100%', marginBottom: 16 }}>
          <Alert
            severity="error"
            sx={{
              width: '100%',
              borderRadius: 2,
              boxShadow: '0 2px 8px rgba(211, 47, 47, 0.2)',
            }}
          >
            {error}
          </Alert>
        </Box>
      )}
      <Box component="form" onSubmit={handleSubmit} sx={authFormSurfaceSx}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Box
                    >
                      <TextField
                        required
                        fullWidth
                        id="firstName"
                        label="First Name"
                        name="firstName"
                        autoComplete="given-name"
                        value={formData.firstName}
                        onChange={handleChange}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <PersonIcon sx={{ color: '#b8860b' }} />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box
                    >
                      <TextField
                        required
                        fullWidth
                        id="lastName"
                        label="Last Name"
                        name="lastName"
                        autoComplete="family-name"
                        value={formData.lastName}
                        onChange={handleChange}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <PersonIcon sx={{ color: '#b8860b' }} />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box>
                      <TextField
                        required
                        fullWidth
                        id="email"
                        label="Email Address"
                        name="email"
                        autoComplete="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        error={!!emailError}
                        helperText={emailError || (emailVerified ? 'Email verified ✓' : '')}
                        disabled={emailVerified}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <EmailIcon sx={{ color: emailVerified ? 'success.main' : '#b8860b' }} />
                            </InputAdornment>
                          ),
                          endAdornment: emailVerified ? (
                            <InputAdornment position="end">
                              <Chip 
                                label="Verified" 
                                color="success" 
                                size="small"
                                icon={<EmailIcon />}
                              />
                            </InputAdornment>
                          ) : null,
                        }}
                      />
                      {!emailVerified && !emailError && isValidEmail(formData.email) && (
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={handleSendEmailOtp}
                          disabled={loading || !!emailError}
                          sx={{
                            mt: 1,
                            width: '100%',
                            borderRadius: 2,
                            borderColor: '#d4a847',
                            color: '#d4a847',
                            '&:hover': {
                              borderColor: '#c49a35',
                              backgroundColor: '#d4a847',
                              color: '#1a1000',
                            },
                          }}
                        >
                          {loading ? 'Sending...' : 'Verify Email'}
                        </Button>
                      )}
                      {!emailVerified && emailOtpSent && (
                        <Box sx={{ mt: 1.5 }}>
                          <TextField
                            fullWidth
                            label="Enter Email OTP"
                            value={emailOtp}
                            onChange={(e) => setEmailOtp(e.target.value)}
                            inputProps={{ maxLength: 6 }}
                          />
                          <Button
                            fullWidth
                            variant="contained"
                            disabled={loading}
                            onClick={handleVerifyEmailOtp}
                            sx={{
                              mt: 1,
                              py: 1.2,
                              borderRadius: 2,
                              background: 'linear-gradient(135deg, #c49a35 0%, #d4a847 40%, #e8c96e 100%)',
                              color: '#1a1000',
                              fontWeight: 'bold',
                              textTransform: 'none',
                            }}
                          >
                            {loading ? 'Verifying...' : 'Verify Email OTP'}
                          </Button>
                        </Box>
                      )}
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box>
                      <TextField
                        required
                        fullWidth
                        id="phoneNumber"
                        label="Phone Number"
                        name="phoneNumber"
                        autoComplete="tel"
                        value={formData.phoneNumber}
                        onChange={handleChange}
                        error={!!phoneError}
                        helperText={phoneError || (phoneVerified ? 'Phone verified ✓' : '')}
                        disabled={phoneVerified}
                        placeholder="10-digit mobile number"
                        inputProps={{ maxLength: 10, inputMode: 'numeric' }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <PhoneIcon sx={{ color: phoneVerified ? 'success.main' : '#b8860b' }} />
                            </InputAdornment>
                          ),
                          endAdornment: phoneVerified ? (
                            <InputAdornment position="end">
                              <Chip 
                                label="Verified" 
                                color="success" 
                                size="small"
                                icon={<PhoneIcon />}
                              />
                            </InputAdornment>
                          ) : null,
                        }}
                      />
                      {!phoneVerified && !phoneError && isValidPhoneNumber(formData.phoneNumber) && (
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={handleSendPhoneOtp}
                          disabled={loading || !!phoneError}
                          sx={{
                            mt: 1,
                            width: '100%',
                            borderRadius: 2,
                            borderColor: '#d4a847',
                            color: '#d4a847',
                            '&:hover': {
                              borderColor: '#c49a35',
                              backgroundColor: '#d4a847',
                              color: '#1a1000',
                            },
                          }}
                        >
                          {loading ? 'Sending...' : 'Verify Phone'}
                        </Button>
                      )}
                      {!phoneVerified && phoneOtpSent && (
                        <Box sx={{ mt: 1.5 }}>
                          <TextField
                            fullWidth
                            label="Enter Phone OTP"
                            value={phoneOtp}
                            onChange={(e) => setPhoneOtp(e.target.value)}
                            inputProps={{ maxLength: 6 }}
                          />
                          <Button
                            fullWidth
                            variant="contained"
                            disabled={loading}
                            onClick={handleVerifyPhoneOtp}
                            sx={{
                              mt: 1,
                              py: 1.2,
                              borderRadius: 2,
                              background: 'linear-gradient(135deg, #c49a35 0%, #d4a847 40%, #e8c96e 100%)',
                              color: '#1a1000',
                              fontWeight: 'bold',
                              textTransform: 'none',
                            }}
                          >
                            {loading ? 'Verifying...' : 'Verify Phone OTP'}
                          </Button>
                        </Box>
                      )}
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box
                    >
                      <FormControl fullWidth required>
                        <InputLabel>User Type</InputLabel>
                        <Select
                          value={formData.userType}
                          label="User Type"
                          onChange={handleUserTypeChange}
                          sx={{
                            borderRadius: '12px',
                            backgroundColor: '#fffcf7',
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'rgba(139, 105, 20, 0.22)',
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'rgba(139, 105, 20, 0.38)',
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#c9a227',
                              borderWidth: '1px',
                            },
                            '&:hover': {
                              boxShadow: '0 4px 18px rgba(46, 38, 28, 0.06)',
                            },
                          }}
                        >
                          <MenuItem value={UserType.CLIENT}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <PersonPinIcon sx={{ color: '#b8860b' }} /> Client
                            </Box>
                          </MenuItem>
                          <MenuItem value={UserType.VENDOR}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <BusinessIcon sx={{ color: '#b8860b' }} /> Vendor
                            </Box>
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: '12px',
                        border: '1px solid rgba(139, 105, 20, 0.2)',
                        backgroundColor: 'rgba(201, 162, 39, 0.06)',
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 600,
                          color: '#5c4a32',
                          mb: 1,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.75,
                        }}
                      >
                        <LockIcon sx={{ fontSize: 18, color: '#b8860b' }} />
                        Create a strong, secure password
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(46, 38, 28, 0.68)', mb: 1.5, lineHeight: 1.45 }}>
                        {PASSWORD_REQUIREMENTS_HINT} Avoid words people could guess (like your name), and do not reuse
                        passwords from other sites.
                      </Typography>
                      {(() => {
                        const r = getPasswordRuleStatus(formData.password);
                        const rows: { ok: boolean; label: string }[] = [
                          { ok: r.minLength, label: 'At least 8 characters' },
                          { ok: r.hasUpper, label: 'One uppercase letter (A–Z)' },
                          { ok: r.hasLower, label: 'One lowercase letter (a–z)' },
                          { ok: r.hasDigit, label: 'One number (0–9)' },
                          { ok: r.hasSpecial, label: 'One symbol (e.g. ! @ # $ % & * ?)' },
                        ];
                        return (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            {rows.map((row) => (
                              <Box
                                key={row.label}
                                sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.125 }}
                              >
                                {row.ok ? (
                                  <CheckCircleIcon sx={{ fontSize: 18, color: '#4a7c59' }} />
                                ) : (
                                  <RadioButtonUncheckedIcon sx={{ fontSize: 18, color: 'rgba(46, 38, 28, 0.28)' }} />
                                )}
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: row.ok ? '#2e5c3a' : 'rgba(46, 38, 28, 0.55)',
                                    fontWeight: row.ok ? 500 : 400,
                                  }}
                                >
                                  {row.label}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        );
                      })()}
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box
                    >
                      <TextField
                        required
                        fullWidth
                        name="password"
                        label="Password"
                        type={showPassword ? 'text' : 'password'}
                        id="password"
                        autoComplete="new-password"
                        value={formData.password}
                        onChange={handleChange}
                        error={!!passwordError}
                        helperText={passwordError || undefined}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <LockIcon sx={{ color: '#b8860b' }} />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => setShowPassword(!showPassword)}
                                edge="end"
                                sx={{ color: '#b8860b' }}
                              >
                                {showPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box
                    >
                      <TextField
                        required
                        fullWidth
                        name="confirmPassword"
                        label="Confirm Password"
                        type={showConfirmPassword ? 'text' : 'password'}
                        id="confirmPassword"
                        value={confirmPassword}
                        onChange={(e) => {
                          setConfirmPassword(e.target.value);
                          setPasswordError('');
                          if (error) setError('');
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <LockIcon sx={{ color: '#b8860b' }} />
                            </InputAdornment>
                          ),
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                edge="end"
                                sx={{ color: '#b8860b' }}
                              >
                                {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Box>
                  </Grid>
                </Grid>
                <Box>
                  {emailVerified && phoneVerified ? (
                    <Button
                      type="button"
                      fullWidth
                      variant="contained"
                      disabled={loading}
                      onClick={handleCompleteSignup}
                      sx={{
                        mt: 3,
                        mb: 2,
                        py: 1.5,
                        borderRadius: 2,
                        background: 'linear-gradient(135deg, #c49a35 0%, #d4a847 40%, #e8c96e 100%)',
                        color: '#1a1000',
                        fontSize: '1.1rem',
                        fontWeight: 'bold',
                        textTransform: 'none',
                        boxShadow: '0 8px 28px rgba(212, 168, 71, 0.3)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #e8c96e 0%, #d4a847 60%, #c49a35 100%)',
                          boxShadow: '0 8px 28px rgba(212, 168, 71, 0.4)',
                          transform: 'translateY(-2px)',
                        },
                        '&:disabled': {
                          background: 'linear-gradient(135deg, #c49a35 0%, #d4a847 40%, #e8c96e 100%)',
                          opacity: 0.7,
                        },
                        transition: 'all 0.3s ease',
                      }}
                    >
                      {loading ? (
                        <CircularProgress size={24} color="inherit" />
                      ) : (
                        'Complete Signup'
                      )}
                    </Button>
                  ) : (
                    <Alert
                      severity="info"
                      sx={{
                        mt: 3,
                        mb: 2,
                        borderRadius: '12px',
                        border: '1px solid rgba(139, 105, 20, 0.2)',
                        backgroundColor: 'rgba(201, 162, 39, 0.08)',
                        color: '#5c4a32',
                        '& .MuiAlert-icon': { color: '#b8860b' },
                      }}
                    >
                      Please verify both your email and phone number before completing signup.
                    </Alert>
                  )}
                </Box>
                <Box
                >
                  <Box textAlign="center">
                    <Typography variant="body2" sx={{ color: 'rgba(46, 38, 28, 0.58)' }}>
                      Already have an account?{' '}
                      <Link
                        component={RouterLink}
                        to="/login"
                        variant="body2"
                        sx={{
                          color: '#9a7318',
                          fontWeight: 'bold',
                          textDecoration: 'none',
                          '&:hover': {
                            textDecoration: 'underline',
                          },
                        }}
                      >
                        Sign in here
                      </Link>
                    </Typography>
                  </Box>
                </Box>
      </Box>
    </AuthShell>
  );
};

export default Signup;
